import { tutorialAssets } from "assets/cdn";
import { useImagePath } from "hooks/useImagePath";
import { Grid, useMediaQuery } from "@mui/material";
import Button from "components/Buttons/ThemeButton";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useEffectOnce } from "react-use";
import { Arrow, Background2, Shine, Text, Tiki2 } from "../../styles";
import { ArrowContainer, Container, SlideNumberContainer } from "./style";

interface Slide5Props {
	onNext: () => void;
	number: number;
	slideNumber: number;
}

export const Slide5 = ({ onNext, number, slideNumber }: Slide5Props) => {
	const { getImage } = useImagePath("cdn");
	const background = getImage(tutorialAssets.background);
	const arrowUp = getImage(tutorialAssets.greenArrow);
	const shineAsset = getImage(tutorialAssets.shine);
	const tiki = getImage(tutorialAssets.char2);
	const containerRef = useRef<HTMLDivElement>(null);
	const isLowHeight = useMediaQuery("(max-height: 599px)");
	const isExtraSmall = useMediaQuery("(max-width: 380px)");
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const [isFirstScroll, setIsFirstScroll] = useState<boolean>(true);
	const [infoButton, setInfoButton] = useState<HTMLElement | null>(null);
	const [infoButtonPosition, setInfoButtonPosition] = useState<DOMRect | null>(
		null
	);
	const [tutorialPosition, setTutorialPosition] = useState<{
		top: number;
		left: number;
	}>({ top: 0, left: 0 });

	const scale = useCalculateScale(
		isPortrait ? 400 : 500,
		isPortrait ? 200 : 400
	);

	// Refs para valores que cambian sin recrear listeners
	const scaleRef = useRef(scale);
	const infoButtonRef = useRef(infoButton);
	const isLowHeightRef = useRef(isLowHeight);
	const isPortraitRef = useRef(isPortrait);
	const isExtraSmallRef = useRef(isExtraSmall);

	useEffect(() => {
		scaleRef.current = scale;
		infoButtonRef.current = infoButton;
		isLowHeightRef.current = isLowHeight;
		isPortraitRef.current = isPortrait;
		isExtraSmallRef.current = isExtraSmall;
	}, [scale, infoButton, isLowHeight, isPortrait, isExtraSmall]);

	const scrollAndHighlight = async () => {
		infoButtonRef?.current?.setAttribute("style", "z-index: 99999;");
		setIsFirstScroll(false);

		const infoButtonPosition = infoButtonRef?.current?.getBoundingClientRect();
		setInfoButtonPosition(infoButtonPosition as DOMRect);
		if (infoButtonPosition && containerRef?.current) {
			const rect = containerRef.current.getBoundingClientRect();
			//const scalePercentageLeft = 1 - scaleRef?.current;
			setTutorialPosition({
				top:
					(infoButtonPosition.top + infoButtonPosition.height + 5) *
					scaleRef?.current,
				left:
					infoButtonPosition.left +
					infoButtonPosition.width / 2 -
					rect.width * 0.93
			});
		}
	};

	useEffectOnce(() => {
		const button = document.getElementById("info-button-loyalty-shop");
		setInfoButton(button);
	});

	useEffect(() => {
		if (infoButton) {
			const infoButtonPosition = infoButton.getBoundingClientRect();
			if (infoButtonPosition) {
				const scrollTop = infoButtonPosition.top + window.scrollY - 65;
				isLowHeight
					? window.scrollTo({
							top: scrollTop,
							behavior: isFirstScroll ? "smooth" : "instant"
					  })
					: infoButton.scrollIntoView({
							block: "center",
							behavior: isFirstScroll ? "smooth" : "instant"
					  });
			}
		}

		return () => {
			infoButton?.removeAttribute("style");
		};
	}, [
		infoButton,
		window.innerHeight,
		window.innerWidth,
		isPortrait,
		isExtraSmall,
		isLowHeight
	]);

	const debouncedHandleScroll = debounce(scrollAndHighlight, 200);

	useEffect(() => {
		window.addEventListener("resize", debouncedHandleScroll);
		window.addEventListener("scrollend", debouncedHandleScroll);
		window.addEventListener("transitionend", debouncedHandleScroll);

		return () => {
			window.removeEventListener("resize", debouncedHandleScroll);
			window.removeEventListener("scrollend", debouncedHandleScroll);
			window.removeEventListener("transitionend", debouncedHandleScroll);
		};
	}, []);

	return (
		<>
			{infoButtonPosition && (
				<>
					<Shine
						src={shineAsset}
						width={130}
						height={130}
						className="animate"
						position="fixed"
						top={`${infoButtonPosition.top + infoButtonPosition.height / 2}px`}
						left={`${infoButtonPosition.left + infoButtonPosition.width / 2}px`}
					/>
					<Container
						ref={containerRef}
						scale={scale}
						top={tutorialPosition.top}
						left={tutorialPosition.left}
					>
						<ArrowContainer>
							<Arrow src={arrowUp} alt="Arrow up" width={46} />
						</ArrowContainer>
						<div>
							<SlideNumberContainer>
								<Text
									color="#fff"
									fontFamily="Baloo"
									fontSize="16px"
									lineHeight="17px"
								>
									{number}/{slideNumber}
								</Text>
							</SlideNumberContainer>

							<Grid item width={331}>
								<Background2 $backgroundImage={background}>
									<div>
										<Tiki2 src={tiki} alt="Tiki" bottom="10px" />
									</div>
									<div>
										<Text
											color="#462C12"
											fontFamily="Inter"
											fontSize="16px"
											fontWeight={700}
											lineHeight="17px"
										>
											Replay the Loyalty Points
											<br /> tutorial anytime.
										</Text>
									</div>
									<Button className="next" onClick={onNext}>
										Finish
									</Button>
								</Background2>
							</Grid>
						</div>
					</Container>
				</>
			)}
		</>
	);
};
